import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ConfirmationService } from 'primeng/api';
import { AlertService } from '@app/_services';
import { ReservationService } from '@app/_components/reservation/reservation.service';

/**
 * Reconcile-Captured popup for the deferred-capture flow.
 *
 * Opened from the order-details page next to "Run Payment" / "Authorize / Capture".
 * Shows the admin a dropdown of payment_confirm rows on this order that are
 * eligible for a manual gateway-side reconcile:
 *   - (AO,A) rows whose auth has not been captured on our side yet
 *   - (PC,D)/(PC,E) rows where the scheduler attempted capture and the gateway
 *     rejected (typical RRC_3_16); admin has since captured manually at the
 *     Authorize.net dashboard.
 *
 * On submit it calls POST /api/2.6/payment/markCapturedFromAuthnet, which
 * verifies via getTransactionDetails before flipping the row to (PC,A) and
 * triggering the downstream cascade (E4, NACHA, owner stmt, QBO sync).
 */
export interface ReconcileCandidate {
	paymentConfirmId: number;
	authnetTxnId:     string;
	amount:           number;
	createDate:       string;
	last4:            string;
	type:             string; // 'AO' | 'PC'
	result:           string; // 'A' | 'D' | 'E'
	billingName:      string;
}

@Component({
	selector: 'app-reconcile-captured-popup',
	templateUrl: './reconcile-captured-popup.component.html',
	styleUrls: ['./reconcile-captured-popup.component.css'],
	standalone: false
})
export class ReconcileCapturedPopupComponent implements OnInit {

	candidates: ReconcileCandidate[] = [];
	selectedPaymentConfirmId: number | null = null;
	submitting = false;

	// Inline status surface — populated from the backend response so the user
	// sees the *actual* MarkCapturedResult.message (e.g. "Authorize.net transaction
	// X is in state 'authorizedPendingCapture' — capture has NOT happened yet.")
	// rather than a generic toast.
	resultSuccess: boolean | null = null;
	resultMessage: string | null  = null;
	resultGatewayStatus: string | null = null;

	@Output() doUpdate = new EventEmitter<any>();

	constructor(
		private alertService: AlertService,
		private confirmationService: ConfirmationService,
		private reservationService: ReservationService,
		private dialogRef: MatDialogRef<ReconcileCapturedPopupComponent>,
		@Inject(MAT_DIALOG_DATA) public data: any
	) {
		this.candidates = (this.data && this.data.candidates) || [];
	}

	ngOnInit(): void {
		// Pre-select the only candidate if there's exactly one — saves a click.
		if (this.candidates.length === 1) {
			this.selectedPaymentConfirmId = this.candidates[0].paymentConfirmId;
		}
	}

	get selectedCandidate(): ReconcileCandidate | null {
		if (this.selectedPaymentConfirmId == null) return null;
		return this.candidates.find(c => c.paymentConfirmId === this.selectedPaymentConfirmId) || null;
	}

	candidateLabel(c: ReconcileCandidate): string {
		const tag = (c.type === 'PC') ? 'CAPTURE FAILED' : 'AWAITING CAPTURE';
		return `[${tag}] $${c.amount} — ${c.billingName} ${c.last4 ? '(' + c.last4 + ')' : ''}`
			+ ` — auth ${c.authnetTxnId} — ${c.createDate}`;
	}

	onSubmit(): void {
		const sel = this.selectedCandidate;
		if (!sel) {
			this.alertService.error('Pick a row to reconcile.');
			return;
		}
		this.confirmationService.confirm({
			header: 'Reconcile Captured',
			message: `Verify with Authorize.net that auth ${sel.authnetTxnId} is captured and mark `
				+ `paymentConfirm ${sel.paymentConfirmId} as (PC, A)? `
				+ `Make sure you've captured it in the Authorize.net dashboard first.`,
			icon: 'fa fa-exclamation-triangle',
			acceptLabel: 'Reconcile',
			rejectLabel: 'Cancel',
			accept: () => {
				this.submitting = true;
				this.resultSuccess       = null;
				this.resultMessage       = null;
				this.resultGatewayStatus = null;

				this.reservationService.markCapturedFromAuthnet(sel.paymentConfirmId, sel.authnetTxnId)
					.subscribe(
						(res: any) => {
							this.submitting = false;
							// Pull the server-side message and gateway status out of the
							// response. ApiResponse shape is { statusCode, message, data: { result: MarkCapturedResult } }.
							const apiMsg     = res?.message;
							const result     = res?.data?.result || res?.result;
							const gwStatus   = result?.gatewayTransactionStatus;
							const resultMsg  = result?.message;
							this.resultMessage       = resultMsg || apiMsg || 'Reconciled.';
							this.resultGatewayStatus = gwStatus || null;

							if (res?.statusCode === 200) {
								this.resultSuccess = true;
								this.alertService.success(this.resultMessage);
								this.doUpdate.emit(sel);
								// Don't auto-close — let the user read the success message
								// (especially the gateway status). They click OK / close to
								// dismiss, which triggers the reload.
							} else {
								this.resultSuccess = false;
								this.alertService.error(this.resultMessage);
							}
						},
						(err: any) => {
							this.submitting = false;
							// IMPORTANT: the global ErrorInterceptor (_helpers/error.interceptor.ts)
							// unwraps non-401 HTTP errors and re-throws the response message as a
							// PLAIN STRING. By the time the error callback fires here, `err` is
							// typically just "Authorize.net could not find transaction X..." — NOT
							// an HttpErrorResponse. Handle both shapes so we always surface the
							// real backend message instead of a generic fallback.
							let msg: string | null = null;
							if (typeof err === 'string') {
								msg = err;
							} else if (err && typeof err === 'object') {
								// Fallback shapes for completeness — some endpoints / older code
								// paths pass the raw HttpErrorResponse through.
								msg = err?.error?.data?.result?.message
									|| err?.error?.message
									|| err?.message
									|| (typeof err?.error === 'string' ? err.error : null);
							}
							const result   = (err && typeof err === 'object')
								? (err?.error?.data?.result || err?.error?.result) : null;
							const gwStatus = result?.gatewayTransactionStatus;
							this.resultSuccess       = false;
							this.resultMessage       = msg || 'Reconcile failed.';
							this.resultGatewayStatus = gwStatus || null;
							this.alertService.error(this.resultMessage);
						});
			}
		});
	}

	onCancel(): void {
		this.dialogRef.close();
		// If the reconcile succeeded, reload on close so the payment list
		// reflects the new (PC, A) state.
		if (this.resultSuccess === true) {
			window.location.reload();
		}
	}

	/** Used by the inline status banner template to clear the message and let the
	 *  user pick a different row without closing + reopening the popup. */
	dismissResult(): void {
		this.resultSuccess       = null;
		this.resultMessage       = null;
		this.resultGatewayStatus = null;
	}
}
